/**
 * GNSS Authentication Service
 * 
 * Manages API key validation, user sessions, and quota tracking
 * Handles authentication for GNSS processing jobs
 */

export interface GNSSApiKey {
  key: string;
  userId: string;
  name: string;
  createdAt: string;
  lastUsedAt?: string;
  expiresAt?: string;
  isActive: boolean;
  tier: 'free' | 'pro' | 'enterprise';
}

export interface GNSSUserQuota {
  userId: string;
  tier: 'free' | 'pro' | 'enterprise';
  monthlyJobsLimit: number;
  monthlyJobsUsed: number;
  monthlyDataLimit: number; // MB
  monthlyDataUsed: number; // MB
  concurrentJobsLimit: number;
  maxJobDuration: number; // seconds
  monthlyResetDate: string;
}

export interface GNSSSession {
  sessionId: string;
  userId: string;
  apiKey: string;
  startedAt: string;
  lastActivityAt: string;
  isActive: boolean;
  userAgent: string;
  ipAddress?: string;
}

export interface AuthenticatedRequest {
  apiKey: string;
  userId: string;
  tier: 'free' | 'pro' | 'enterprise';
  quota: GNSSUserQuota;
  session: GNSSSession;
}

/**
 * Authentication and quota management service
 */
class GNSSAuthService {
  private storagePrefix = 'gnss_auth_';
  private sessionStoragePrefix = 'gnss_session_';

  /**
   * Store API key securely (encrypted in localStorage)
   */
  storeApiKey(key: string, userId: string, name: string, tier: 'free' | 'pro' = 'free'): GNSSApiKey {
    const apiKeyObj: GNSSApiKey = {
      key,
      userId,
      name,
      tier,
      createdAt: new Date().toISOString(),
      isActive: true,
    };

    // Store in localStorage with encryption (basic encoding for demo)
    const encoded = btoa(JSON.stringify(apiKeyObj));
    localStorage.setItem(`${this.storagePrefix}key`, encoded);

    return apiKeyObj;
  }

  /**
   * Retrieve stored API key
   */
  getStoredApiKey(): GNSSApiKey | null {
    const encoded = localStorage.getItem(`${this.storagePrefix}key`);
    if (!encoded) return null;

    try {
      return JSON.parse(atob(encoded));
    } catch {
      return null;
    }
  }

  /**
   * Validate API key format and expiration
   */
  validateApiKey(key: string): { valid: boolean; error?: string } {
    if (!key || typeof key !== 'string') {
      return { valid: false, error: 'API key is required' };
    }

    if (key.length < 32) {
      return { valid: false, error: 'Invalid API key format' };
    }

    const stored = this.getStoredApiKey();
    if (!stored) {
      return { valid: false, error: 'No API key configured' };
    }

    if (stored.key !== key) {
      return { valid: false, error: 'API key mismatch' };
    }

    if (!stored.isActive) {
      return { valid: false, error: 'API key is inactive' };
    }

    if (stored.expiresAt && new Date(stored.expiresAt) < new Date()) {
      return { valid: false, error: 'API key has expired' };
    }

    return { valid: true };
  }

  /**
   * Create a new session for authenticated user
   */
  createSession(apiKey: string): GNSSSession | null {
    const apiKeyObj = this.getStoredApiKey();
    if (!apiKeyObj) return null;

    const validation = this.validateApiKey(apiKey);
    if (!validation.valid) return null;

    const session: GNSSSession = {
      sessionId: this.generateSessionId(),
      userId: apiKeyObj.userId,
      apiKey,
      startedAt: new Date().toISOString(),
      lastActivityAt: new Date().toISOString(),
      isActive: true,
      userAgent: navigator.userAgent,
    };

    // Store session (not encrypted since it's temporary)
    sessionStorage.setItem(`${this.sessionStoragePrefix}${session.sessionId}`, JSON.stringify(session));

    return session;
  }

  /**
   * Get current active session
   */
  getCurrentSession(): GNSSSession | null {
    // Find first active session
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(this.sessionStoragePrefix)) {
        try {
          const session = JSON.parse(sessionStorage.getItem(key) || '{}');
          if (session.isActive) {
            return session;
          }
        } catch {
          // Skip invalid sessions
        }
      }
    }
    return null;
  }

  /**
   * Update session activity timestamp
   */
  updateSessionActivity(sessionId: string): boolean {
    const session = this.getSession(sessionId);
    if (!session) return false;

    session.lastActivityAt = new Date().toISOString();
    sessionStorage.setItem(`${this.sessionStoragePrefix}${sessionId}`, JSON.stringify(session));

    return true;
  }

  /**
   * Invalidate session
   */
  invalidateSession(sessionId: string): boolean {
    const session = this.getSession(sessionId);
    if (!session) return false;

    session.isActive = false;
    sessionStorage.setItem(`${this.sessionStoragePrefix}${sessionId}`, JSON.stringify(session));

    return true;
  }

  /**
   * Get user quota
   */
  getUserQuota(tier: 'free' | 'pro' | 'enterprise' = 'free'): GNSSUserQuota {
    const stored = this.getStoredApiKey();
    const userId = stored?.userId || 'anonymous';

    // Define quota tiers
    const quotas: Record<string, Omit<GNSSUserQuota, 'userId' | 'monthlyJobsUsed' | 'monthlyDataUsed' | 'monthlyResetDate'>> = {
      free: {
        tier: 'free',
        monthlyJobsLimit: 10,
        monthlyDataLimit: 500, // 500 MB
        concurrentJobsLimit: 1,
        maxJobDuration: 3600, // 1 hour
      },
      pro: {
        tier: 'pro',
        monthlyJobsLimit: 100,
        monthlyDataLimit: 5000, // 5 GB
        concurrentJobsLimit: 5,
        maxJobDuration: 14400, // 4 hours
      },
      enterprise: {
        tier: 'enterprise',
        monthlyJobsLimit: 10000,
        monthlyDataLimit: 100000, // 100 GB
        concurrentJobsLimit: 50,
        maxJobDuration: 86400, // 24 hours
      },
    };

    const baseQuota = quotas[tier] || quotas.free;
    const stored_quota = this.getStoredQuota(userId);

    return {
      userId,
      ...baseQuota,
      monthlyJobsUsed: stored_quota?.monthlyJobsUsed || 0,
      monthlyDataUsed: stored_quota?.monthlyDataUsed || 0,
      monthlyResetDate: stored_quota?.monthlyResetDate || this.getNextMonthReset(),
    };
  }

  /**
   * Check if quota allows processing
   */
  canProcessJob(fileSize: number, quota: GNSSUserQuota): { allowed: boolean; reason?: string } {
    if (quota.monthlyJobsUsed >= quota.monthlyJobsLimit) {
      return {
        allowed: false,
        reason: `Monthly job limit reached (${quota.monthlyJobsUsed}/${quota.monthlyJobsLimit})`,
      };
    }

    const fileSizeMB = fileSize / (1024 * 1024);
    if (quota.monthlyDataUsed + fileSizeMB > quota.monthlyDataLimit) {
      return {
        allowed: false,
        reason: `Insufficient data quota (${(quota.monthlyDataLimit - quota.monthlyDataUsed).toFixed(1)} MB remaining)`,
      };
    }

    return { allowed: true };
  }

  /**
   * Record job usage
   */
  recordJobUsage(userId: string, fileSize: number): void {
    const stored_quota = this.getStoredQuota(userId);
    const fileSizeMB = fileSize / (1024 * 1024);

    const updated_quota: GNSSUserQuota = {
      userId,
      tier: (this.getStoredApiKey()?.tier || 'free') as any,
      monthlyJobsLimit: stored_quota?.monthlyJobsLimit || 10,
      monthlyJobsUsed: (stored_quota?.monthlyJobsUsed || 0) + 1,
      monthlyDataLimit: stored_quota?.monthlyDataLimit || 500,
      monthlyDataUsed: (stored_quota?.monthlyDataUsed || 0) + fileSizeMB,
      concurrentJobsLimit: stored_quota?.concurrentJobsLimit || 1,
      maxJobDuration: stored_quota?.maxJobDuration || 3600,
      monthlyResetDate: stored_quota?.monthlyResetDate || this.getNextMonthReset(),
    };

    // Reset quota if month has passed
    if (new Date() > new Date(updated_quota.monthlyResetDate)) {
      updated_quota.monthlyJobsUsed = 1;
      updated_quota.monthlyDataUsed = fileSizeMB;
      updated_quota.monthlyResetDate = this.getNextMonthReset();
    }

    this.storeQuota(updated_quota);
  }

  /**
   * Get usage percentage
   */
  getUsagePercentage(quota: GNSSUserQuota): { jobs: number; data: number } {
    return {
      jobs: (quota.monthlyJobsUsed / quota.monthlyJobsLimit) * 100,
      data: (quota.monthlyDataUsed / quota.monthlyDataLimit) * 100,
    };
  }

  /**
   * Clear all auth data (logout)
   */
  clearAuthData(): void {
    // Clear API key
    localStorage.removeItem(`${this.storagePrefix}key`);

    // Clear all sessions
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(this.sessionStoragePrefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  }

  /**
   * Export auth data for debugging
   */
  exportAuthData() {
    const apiKey = this.getStoredApiKey();
    const currentSession = this.getCurrentSession();
    const quota = apiKey ? this.getUserQuota(apiKey.tier) : null;

    return {
      apiKeyUser: apiKey?.userId,
      apiKeyTier: apiKey?.tier,
      apiKeyActive: apiKey?.isActive,
      currentSession: currentSession ? {
        userId: currentSession.userId,
        startedAt: currentSession.startedAt,
        lastActivityAt: currentSession.lastActivityAt,
      } : null,
      quota,
    };
  }

  // Private helper methods

  private generateSessionId(): string {
    return `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private getSession(sessionId: string): GNSSSession | null {
    const data = sessionStorage.getItem(`${this.sessionStoragePrefix}${sessionId}`);
    if (!data) return null;

    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }

  private getStoredQuota(userId: string): GNSSUserQuota | null {
    const data = localStorage.getItem(`${this.storagePrefix}quota_${userId}`);
    if (!data) return null;

    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }

  private storeQuota(quota: GNSSUserQuota): void {
    localStorage.setItem(`${this.storagePrefix}quota_${quota.userId}`, JSON.stringify(quota));
  }

  private getNextMonthReset(): string {
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return nextMonth.toISOString();
  }
}

// Singleton instance
const authService = new GNSSAuthService();

export default authService;
